import type { LucideIcon } from 'lucide-react';
import { CalendarOff, CalendarRange, ChartColumn, History, Plane, Settings, UserRound, Users } from 'lucide-react';
import type { MemberRole } from '../data/database.types';

/** The scheduler module's role, derived from module access (D83). */
export type Role = MemberRole;

/**
 * The scheduler module's pages (V2 D80, D83). They live under /scheduler;
 * the portal home and People pages sit outside this list. Schedulers get
 * six; viewers get My schedule only (D79).
 */
export type PageId = 'schedule' | 'history' | 'pilots' | 'unavailability' | 'fleet' | 'utilization' | 'settings' | 'my-schedule';

export const SCHEDULER_BASE = '/scheduler';

export interface PageDef {
  id: PageId;
  path: string;
  label: string;
  icon: LucideIcon;
  /** Lowest role that sees the page. */
  minRole: Role;
  /** Keyboard chord after `g` (spec 5.13). */
  key?: string;
}

export const ROLE_RANK: Record<Role, number> = { viewer: 0, scheduler: 1, admin: 2 };

export const PAGES: readonly PageDef[] = [
  { id: 'schedule', path: `${SCHEDULER_BASE}/schedule`, label: 'Schedule', icon: CalendarRange, minRole: 'scheduler', key: 's' },
  { id: 'history', path: `${SCHEDULER_BASE}/published`, label: 'Post', icon: History, minRole: 'scheduler', key: 'b' },
  { id: 'pilots', path: `${SCHEDULER_BASE}/pilots`, label: 'Pilots', icon: Users, minRole: 'scheduler', key: 'p' },
  { id: 'unavailability', path: `${SCHEDULER_BASE}/unavailability`, label: 'Unavailability', icon: CalendarOff, minRole: 'scheduler', key: 't' },
  { id: 'fleet', path: `${SCHEDULER_BASE}/fleet`, label: 'Fleet', icon: Plane, minRole: 'scheduler', key: 'f' },
  { id: 'utilization', path: `${SCHEDULER_BASE}/utilization`, label: 'Utilization', icon: ChartColumn, minRole: 'scheduler', key: 'u' },
  { id: 'settings', path: `${SCHEDULER_BASE}/settings`, label: 'Settings', icon: Settings, minRole: 'scheduler', key: ',' },
  { id: 'my-schedule', path: `${SCHEDULER_BASE}/me`, label: 'My schedule', icon: UserRound, minRole: 'viewer', key: 'm' },
];

export function canSee(role: Role, page: PageDef): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[page.minRole];
}

export function isScheduler(role: Role | null | undefined): boolean {
  return role === 'scheduler' || role === 'admin';
}

/** Where a person lands when they open the Scheduler tile. On a phone that is always My schedule (D85). */
export function homePathFor(role: Role, phone = false): string {
  return isScheduler(role) && !phone ? `${SCHEDULER_BASE}/schedule` : `${SCHEDULER_BASE}/me`;
}

/** Resolve the current page from a pathname (so the help drawer knows the page). */
export function pageForPath(pathname: string): PageDef | undefined {
  const exact = PAGES.find((p) => p.path === pathname);
  if (exact) return exact;
  return PAGES.find((p) => pathname.startsWith(p.path + '/'));
}
